import { getAverageNumberOfResidentsQueryOptions } from '@/entities/facility/api/get-average-number-of-residents.query';
import { FocusSummaryCard } from '@/shared/components';
import { useFacilityScope } from '@/shared/lib/hooks/use-facility-scope';
import { formatNumber } from '@/shared/lib/number';
import { sparklineToChartData } from '@/shared/lib/sparkline-chart';
import { useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import type { FC } from 'react';

type NumberOfResidentsCardProps = {
  dateRange: {
    from: Dayjs;
    to: Dayjs;
  };
};

/**
 * Average residents for the selected period, on the Facilities Operational tab.
 *
 * Distinct from `AverageResidentsCard` in `facility-residents-cards`, which is hard-scoped to
 * one facility and carries no sparkline. This one follows the global facility scope, so it
 * works in Portfolio Mode and Single Mode alike.
 */
export const NumberOfResidentsCard: FC<NumberOfResidentsCardProps> = ({
  dateRange,
}) => {
  const { scopedFacilityId } = useFacilityScope();

  const { data, isLoading } = useQuery(
    getAverageNumberOfResidentsQueryOptions({
      from: dateRange.from.format('YYYY-MM-DD'),
      to: dateRange.to.format('YYYY-MM-DD'),
      facilitiesIds: scopedFacilityId ? [scopedFacilityId] : undefined,
    })
  );

  const indicator =
    data?.avgResidentsChangePct != null
      ? {
          value: data.avgResidentsChangePct,
          negativeValue: data.avgResidentsChangePct < 0,
        }
      : undefined;

  return (
    <FocusSummaryCard
      showChart
      icon="team"
      title="Number of Residents"
      tooltipMessage="Average number of residents across facilities during the selected period"
      indicator={indicator}
      value={
        data?.avgResidents != null ? formatNumber(data.avgResidents) : 'N/A'
      }
      chart={
        data?.sparkline
          ? { data: sparklineToChartData(data.sparkline) }
          : undefined
      }
      loading={isLoading}
    />
  );
};
